<template>
  <h2 class="form-title">Create assignment</h2>
  <VeeForm class="assignment-form table-shadow mb-5" @submit="submit">
    <Customer v-if="isStaff" />
    <SenderAddress />
    <AssignmentType />
    <DistributionChannel />
    <UploadFile v-if="assignment.assignment_type === CONSOLIDATE" />
    <RecipientAddress v-if="assignment.assignment_type === SINGLE" />
    <Pieces v-if="assignment.assignment_type === SINGLE" :key="assignment.distribution_channel" />
    <ServiceLevel />
    <PickupBlock />
    <OrderInfo />
    <div class="form-group mt-4 mb-0">
      <b-button type="submit" class="btn create-btn btn-green"> Create assignment </b-button>
    </div>
  </VeeForm>
  <SourceFileErrorsModal :id="CREATE_ASSIGNMENT_SOURCE_FILE_ERRORS" />
  <RequestErrorModal :id="REQUEST_ERROR" />
</template>

<script lang="ts">
import type { AssignmentCreationData, AssignmentResponse } from '@/types/Models/Assignment'
import { BButton } from 'bootstrap-vue-next'
import { Form as VeeForm } from 'vee-validate'
import { computed, defineComponent, type ComputedRef, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { CONSOLIDATE, SINGLE } from '@/const/AssignmentTypes'
import {
  CREATE_ASSIGNMENT_SOURCE_FILE_ERRORS,
  SENDER_ADDRESS_ACTION,
  CREATE_NEW_CUSTOMER,
  REQUEST_ERROR,
} from '@/const/ModalTypes'
import { none } from '@/services/Helpers'
import { registerModals } from '@/services/Modal'
import { useAccountStore } from '@/store/account'
import { useAssignmentFormStore } from '@/store/assignmentForm'
import { useAssignmentSummaryStore } from '@/store/assignmentSummary'
import { CUSTOMER_ASSIGNMENTS_DETAILS, STAFF_ASSIGNMENTS_DETAILS } from '@/types/router'
import Pieces from '@/views/Components/Assignment/Pieces/Pieces.vue'
import Customer from '@/views/Components/Assignment/CreateEditForm/Customer.vue'
import OrderInfo from '@/views/Components/Assignment/CreateEditForm/OrderInfo.vue'
import UploadFile from '@/views/Components/Assignment/CreateEditForm/UploadFile.vue'
import PickupBlock from '@/views/Components/Assignment/CreateEditForm/Pickup.vue'
import ServiceLevel from '@/views/Components/Assignment/CreateEditForm/ServiceLevel.vue'
import SenderAddress from '@/views/Components/Assignment/CreateEditForm/SenderAddress.vue'
import AssignmentType from '@/views/Components/Assignment/CreateEditForm/AssignmentType.vue'
import RecipientAddress from '@/views/Components/Assignment/CreateEditForm/RecipientAddress.vue'
import RequestErrorModal from '@/views/Modals/RequestErrorModal.vue'
import DistributionChannel from '@/views/Components/Assignment/CreateEditForm/DistributionChannel.vue'
import SourceFileErrorsModal from '@/views/Modals/Assignment/SourceFileErrorsModal.vue'

export default defineComponent({
  name: 'CreateEditForm',
  components: {
    SourceFileErrorsModal,
    DistributionChannel,
    RequestErrorModal,
    RecipientAddress,
    AssignmentType,
    SenderAddress,
    ServiceLevel,
    PickupBlock,
    UploadFile,
    OrderInfo,
    Customer,
    BButton,
    VeeForm,
    Pieces,
  },
  setup() {
    const accountStore = useAccountStore()
    const router = useRouter()
    const assignmentFormStore = useAssignmentFormStore()
    const assignmentSummaryStore = useAssignmentSummaryStore()

    const isStaff: ComputedRef<boolean> = computed(() => accountStore.isStaff)
    const companyId = computed(() => accountStore.companyId)
    const assignment: ComputedRef<AssignmentCreationData> = computed(() => assignmentFormStore.assignment)

    const submit = () => {
      const companyIdError = isStaff.value && assignment.value.company_id === '' ? 'Select client, please.' : ''
      if (assignment.value.assignment_type !== SINGLE) {
        assignmentFormStore.setErrors({ company_id: companyIdError, file: '' })
      }
      if (companyIdError) return
      assignmentFormStore.submit(isStaff.value).then((raw?: AssignmentResponse) => {
        if (!raw?.data) return
        assignmentSummaryStore.setAssignment(raw)
        router
          .push({
            name: isStaff.value ? STAFF_ASSIGNMENTS_DETAILS : CUSTOMER_ASSIGNMENTS_DETAILS,
            params: { id: raw.data.id },
          })
          .then(none)
      })
    }

    onMounted(() => {
      registerModals(REQUEST_ERROR)
      registerModals(CREATE_NEW_CUSTOMER)
      registerModals(SENDER_ADDRESS_ACTION)
      registerModals(CREATE_ASSIGNMENT_SOURCE_FILE_ERRORS)

      assignmentFormStore.reset()
      assignmentFormStore.setFields({
        assignment_type: router.currentRoute.value.query?.assignmentType === SINGLE ? SINGLE : CONSOLIDATE,
        company_id: !isStaff.value ? companyId.value : '',
      })
    })

    watch(
      () => companyId.value,
      (value) => {
        if (!isStaff.value) {
          assignmentFormStore.setFields({ company_id: value })
        }
      },
    )

    return {
      submit,
      isStaff,
      assignment,
      SINGLE,
      CONSOLIDATE,
      REQUEST_ERROR,
      CREATE_ASSIGNMENT_SOURCE_FILE_ERRORS,
    }
  },
})
</script>
